import { RouterActions, RouterSelectors } from "@app/root-store";
import { Store, select } from "@ngrx/store";
import { filter, map, tap, withLatestFrom } from "rxjs/operators";

import { Injectable } from "@angular/core";
import { SnackbarActions } from "@app/root-store/actions";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { BehaviorSubject, combineLatest } from "rxjs";
import { AuthActions } from "../store/actions";
import { AuthState } from "../store/auth.state";
import { AuthSelectors } from "../store/selectors";

@Injectable({
    providedIn: "root",
})
export class AuthUserService {
    constructor(private readonly store: Store<AuthState>) {}
    expiredMessage$ = new BehaviorSubject<string>("");
    /**
     * Reset login form errors
     *
     * @description Dispatch an action to reset any login form errors
     */
    clearLoginErrorState() {
        this.store.dispatch(AuthActions.clearError());
    }

    selectAuthUser$() {
        return this.store.pipe(select(AuthSelectors.selectGetUser));
    }

    selectAuthUserEntityId$() {
        return this.store.pipe(select(AuthSelectors.selectAuthUserEntityId)).pipe(filter(id => !!id));
    }

    selectPendingSignInState$() {
        return this.store.pipe(select(AuthSelectors.selectGetPending));
    }

    selectLoggedInState$() {
        return this.store.pipe(select(AuthSelectors.selectGetLoggedIn));
    }

    selectSignInError$() {
        return this.store.pipe(select(AuthSelectors.selectGetError));
    }

    selectSignInErrorCode$() {
        return this.store.pipe(select(AuthSelectors.selectGetErrorCode));
    }

    selectCurrentUrl$() {
        return this.store.pipe(select(RouterSelectors.getUrl));
    }

    selectCurrentUrlQueryParams$() {
        return this.store.pipe(select(RouterSelectors.getQueryParams));
    }

    setSignInFailure(e) {
        this.store.dispatch(AuthActions.logInFailure(e));
    }

    signIn() {
        this.store.dispatch(AuthActions.logIn());
    }

    signOutConfirmation() {
        this.store.dispatch(AuthActions.logOutConfirmation());
    }

    signOut(redirectTo: string) {
        this.store.dispatch(AuthActions.logOut({ redirectTo }));
    }

    redirectToSignIn(redirectTo: string) {
        this.store.dispatch(AuthActions.logInRedirect({ redirectTo }));
    }

    resetPassword(data: { email: string }) {
        this.store.dispatch(AuthActions.resetPassword(data));
    }

    isValidInviteCode(inviteCode: string): boolean {
        return inviteCode && inviteCode.length === 9 && inviteCode !== "undefined";
    }

    /**
     * Waits for the sign in to beo completed and returns the authorized user
     *
     * @returns The authorized user object once sign in is completed
     */
    getAuthUser() {
        return this.selectAuthUser$().pipe(
            withLatestFrom(this.selectPendingSignInState$()),
            filter(([authUser, isPending]) => !isPending || !!authUser),
            map(([authUser]) => authUser)
        );
    }

    /**
     * Dispatches an error message
     *
     * @param message The error message
     */
    dispatchError(message: string) {
        this.store.dispatch(
            SnackbarActions.openSnackbar({
                message: new HttpResponseHandler(null, null, message, true),
            })
        );
    }

    /**
     * Redirect the user to the mobile refresh page. Pass along the page to load when the user refreshes.
     *
     * @param redirectTo The page to reload when the user hits the reload button
     */
    dispatchMobileReload(redirectTo: string) {
        this.store.dispatch(RouterActions.routerGo({ path: ["mobile-reload"], query: { redirectTo } }));
    }

    /**
     * Dispatch an action to register a new Firebase user
     *
     * @param displayName The display name for the new user
     * @param email The email address of the new user
     * @param password The password of the new user
     * @param inviteCode The inviteCode used to look up the user in the database
     */
    dispatchRegister(displayName: string, email: string, password: string, inviteCode: string) {
        this.store.dispatch(AuthActions.register({ displayName, email, password, inviteCode }));
    }

    /**
     * Used to display an error to the user regarding registration.
     *
     * @param message The error message to display
     */
    dispatchRegisterFailure(message: string) {
        this.store.dispatch(AuthActions.registerFailure({ message }));
    }

    dispatchVerifyPassword(requestData) {
        this.store.dispatch(AuthActions.verifyPassword(requestData));
    }

    dispatchChangePassword(requestData) {
        this.store.dispatch(AuthActions.changePassword(requestData));
    }

    dispatchStopPendingLogin() {
        this.store.dispatch(AuthActions.stopPendingLogin());
    }

    dispatchClearErrorPendingTrue() {
        this.store.dispatch(AuthActions.clearErrorPendingTrue());
    }

    dispatchValidateInviteCode$(requestData) {
        return combineLatest([
            this.store.pipe(select(AuthSelectors.selectInviteCodeValid)),
            this.store.pipe(select(AuthSelectors.selectGetError)),
        ]).pipe(
            tap(([isValid]) => {
                if (isValid === null) {
                    this.store.dispatch(AuthActions.validateInviteCode(requestData));
                }
            }),
            filter(([isValid, errorMessage]) => isValid === true || (isValid === false && !!errorMessage)),
            map(([isValid, errorMessage]) => ({ isValid, errorMessage }))
        );
    }
    dispatchSessionExpired(message: string) {
        this.expiredMessage$.next(message);
    }
}
